import { createRef, useEffect } from 'react';
import Image from 'next/image';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import { ArrowLeft, ArrowRight } from '@/icons';
import { IconButton } from '@/molecules';

import type { Options } from '@splidejs/splide';
import type { FunctionComponent, ReactNode } from 'react';
import type { TypeGalleryProps } from '.';

/**
 * The main Splide component.
 */
export const mainRef = createRef<Splide>();

// TODO: Reemplazar la implementación custom de las flechas con SplideTrack
// @see https://github.com/Splidejs/splide/discussions/769#discussioncomment-2639518
export const Gallery: FunctionComponent<TypeGalleryProps> = ({
  slides,
}: TypeGalleryProps): JSX.Element => {
  /**
   * The thumbnail Splide component.
   */
  const thumbsRef = createRef<Splide>();

  const prevSlide = () => {
    mainRef.current?.splide?.go('<');
  };

  const nextSlide = () => {
    mainRef.current?.splide?.go('>');
  };

  /**
   * Set the sync target right after the component is mounted.
   */
  useEffect(() => {
    if (mainRef.current && thumbsRef.current && thumbsRef.current.splide) {
      mainRef.current.sync(thumbsRef.current.splide);
    }
  });

  // Temporal fix, source:https://github.com/Splidejs/splide/issues/712#issuecomment-1067954268
  // it's not optimal and the developer doesn't like it either but it works for now,
  // maybe fixed in version 4?
  useEffect(() => {
    mainRef?.current?.splide?.refresh();
  });

  /**
   * Render slides.
   *
   * @return Slide nodes.
   */
  const renderSlides = (aspectRatio = ''): ReactNode[] =>
    slides.map((slide, index) => (
      <SplideSlide
        key={`${index}_${slide.src}`}
        className={aspectRatio}
        {...(slide?.id && { 'data-id': slide.id })}
      >
        <div className="flex items-center justify-center">
          <Image
            priority={index ? false : true}
            src={slide.src}
            alt={slide.alt ?? ''}
            width={slide.width}
            height={slide.height}
            className="object-contain object-center"
          />
        </div>
      </SplideSlide>
    ));

  const mainOptions: Options = {
    type: slides.length === 1 ? 'slide' : 'loop',
    perPage: 1,
    perMove: 1,
    gap: '1rem',
    pagination: slides.length > 1,
    drag: slides.length > 1,
    arrows: false,
    classes: {
      pagination:
        'flex flex-nowrap gap-2 md:hidden bottom-0 p-0 [&_li]:flex [&_li]:w-full',
      page: 'h-0.5 w-full m-0 bg-primary-900 rounded-none opacity-100 [&.is-active]:bg-secondary-500 hover:bg-secondary-500 [&.is-active]:scale-[1] [&.is-active]:opacity-100',
    },
    lazyLoad: 'sequential',
  };

  const thumbsOptions: Options = {
    type: 'slide',
    rewind: true,
    gap: '0.5rem',
    pagination: false,
    fixedWidth: 64,
    fixedHeight: 64,
    cover: true,
    focus: 'center',
    isNavigation: true,
    arrows: false,
    updateOnMove: true,
    lazyLoad: 'sequential',
  };

  /**
   * Render the component.
   *
   * @return A React node.
   */
  return (
    <>
      {/**
       * TODO: La versión que tenemos de Splide no tiene el prop Children ya que en React 17 se añadia automaticamente pero en la 18 no
       * Cuando actualicemos Splide a la última versión, podemos quitar el @ts-ignore
       * @eslint-disable-next-line @typescript-eslint/ban-ts-comment
       * @ts-expect-error types are not for react 18 */}
      <Splide options={mainOptions} ref={mainRef} className="pb-4">
        {renderSlides('aspect-w-1 aspect-h-1')}
      </Splide>
      {slides.length > 1 ? (
        <div className="hidden md:grid md:grid-cols-3 md:items-center md:gap-4">
          <div className="flex justify-center gap-2 [&>*]:flex-shrink-0">
            <IconButton style="dashed-outline" onClick={prevSlide} size="big">
              <ArrowLeft />
            </IconButton>
            <IconButton style="dashed-outline" onClick={nextSlide} size="big">
              <ArrowRight />
            </IconButton>
          </div>

          {/**
           * TODO: La versión que tenemos de Splide no tiene el prop Children ya que en React 17 se añadia automaticamente pero en la 18 no
           * Cuando actualicemos Splide a la última versión, podemos quitar el @ts-ignore
           * @eslint-disable-next-line @typescript-eslint/ban-ts-comment
           * @ts-expect-error types are not for react 18 */}
          <Splide
            options={thumbsOptions}
            ref={thumbsRef}
            className="col-span-2 [&_li.splide\_\_slide.is-active]:!border-none [&_li.splide\_\_slide.is-active]:opacity-100 [&_li.splide\_\_slide]:!border-none [&_li.splide\_\_slide]:opacity-40 hover:[&_li.splide\_\_slide]:opacity-100"
          >
            {renderSlides()}
          </Splide>
        </div>
      ) : null}
    </>
  );
};
